import React from 'react';
import { HallowComponent } from "../HallowComponent";
import { HallowImage } from "../HallowImage";
import Timebar from "./Timebar";
import "./style.css";

class HallowAudio extends HallowComponent {
    // required props: id, url, duration ('#:##')
    // optional props: type, start, end

    _audio; // the audio file element
    _playButton; // the play button element
    _pauseButton; // the pause button element
    currentlyScrubbing = false;
    start = this.props.start || 0;
    end = this.props.end || 120000;
    state = {
        markerLeft: 0
    }

    get duration() {
        let duration = this.props.duration;
        if (duration) return duration;
        if (!this._audio || !this._audio.duration) return 0;
        duration = this._audio.duration * 1000;
        return duration;
    }

    //#region Static Functions
    makePlaySvg() {
        return <path d="M2 14V2L14 8L2 14Z" fill="black" stroke="black" strokeWidth="4" strokeLinejoin="round"/>;
    }

    makePauseSvg() {
        return (<svg>
            <rect id="ee-background" x="0" y="0" width="16" height="16"
                  style={{fill: 'white', fillOpacity: 0, pointerEvents: 'none'}} transform="matrix(1, 0, 0, 1, 0, 6)"/>
            <g transform="matrix(0.3500001132488251, 0, 0, 0.3499999940395355, 1.290645718574524, 0.7646169662475586)">
                <path fillRule="evenodd" clipRule="evenodd"
                      d="M4 2C2.89543 2 2 2.89543 2 4V36C2 37.1046 2.89543 38 4 38H12C13.1046 38 14 37.1046 14 36V4C14 2.89543 13.1046 2 12 2H4ZM28 2C26.8954 2 26 2.89543 26 4V36C26 37.1046 26.8954 38 28 38H36C37.1046 38 38 37.1046 38 36V4C38 2.89543 37.1046 2 36 2H28Z"
                      fill="white" style={{fill: 'rgb(0, 0, 0)',}}/>
                <path
                    d="M4 4V4V0C1.79086 0 0 1.79086 0 4H4ZM4 36V4H0V36H4ZM4 36H4H0C0 38.2091 1.79086 40 4 40V36ZM12 36H4V40H12V36ZM12 36V40C14.2091 40 16 38.2091 16 36H12ZM12 4V36H16V4H12ZM12 4H16C16 1.79086 14.2091 0 12 0V4ZM4 4H12V0H4V4ZM28 4V4V0C25.7909 0 24 1.79086 24 4H28ZM28 36V4H24V36H28ZM28 36H24C24 38.2091 25.7909 40 28 40V36ZM36 36H28V40H36V36ZM36 36V40C38.2091 40 40 38.2091 40 36H36ZM36 4V36H40V4H36ZM36 4H40C40 1.79086 38.2091 0 36 0V4ZM28 4H36V0H28V4Z"
                    fill="white" style={{fill: 'rgb(0, 0, 0)'}}/>
            </g>
        </svg>);
    }

    makePauseStyle() {
        return {
            shapeRender: 'geometricPrecision',
            textRendering: 'geometricPrecision',
            imageRendering: 'optimizeQuality',
            fillRule: 'evenodd',
            clipRule: 'evenodd'
        };
    }
    //#endregion

    //#region Event Handlers
    handleTimeUpdates = () => {
        if (this.currentlyScrubbing) return;
        let time = this._audio.currentTime * 1000;
        let relativeTime = time - this.start;
        let additionsToState = {
            markerLeft: (relativeTime / this.duration * 100) + '%'
        };
        this.setState(additionsToState, () => {
            this._timebar.moveMarker(this.state.markerLeft, relativeTime);
            if (time >= this.end) {
                this.handlePause();
            }
        });
    }

    handlePlay = () => {
        let time = this._audio.currentTime * 1000;
        if (this.props.type === 'sample') {
            if (time < this.start || time >= this.end) {
                this._audio.currentTime = Math.floor(this.start / 1000);
            }
        }
        this._audio.play();
        this._playButton.classList.add('hidden');
        this._pauseButton.classList.remove('hidden');
    }

    handlePause = () => {
        this._audio.pause();
        this._playButton.classList.remove('hidden');
        this._pauseButton.classList.add('hidden');
    }

    handleSliderMove = () => {
        this.currentlyScrubbing = true;
    }

    handleSliderChange = (percentString) => {
        this._audio.currentTime = this.getMsAtPercent(percentString) / 1000;
        this.currentlyScrubbing = false;
    }

    //#endregion

    getMsAtPercent = (percentString) => {
        let multiplier = parseFloat(percentString) / 100;
        return this.duration * multiplier;
    }

    render() {
        let controls;
        let playButton = (<button
            id={this.props.id + '_play'}
            className='play'
            onClick={this.handlePlay}
            ref={c => this._playButton = c}
            key={this.generateChildKey('playButton')} >
            <HallowImage
                makeSvg={this.makePlaySvg}
                width='16px'
                height='16px'
                {...this.generateKeyProps('playSvg')} />
        </button>);
        let pauseButton = (<button
            id={this.props.id + '_pause'}
            className='pause hidden'
            onClick={this.handlePause}
            ref={c => this._pauseButton = c}
            key={this.generateChildKey('pauseButton')} >
            <HallowImage
                makeSvg={this.makePauseSvg}
                width='16px'
                height='16px'
                makeStyle={this.makePauseStyle}
                {...this.generateKeyProps('pauseSvg')} />
        </button>);
        let timeBar = <Timebar id={this.props.id + '_timebar'} markerLeft={this.state.markerLeft}
                               duration={this.duration} onPickUp={this.handleSliderMove}
                               onChange={this.handleSliderChange}
                               getMs={this.getMsAtPercent} ref={c => this._timebar = c}
                               {...this.generateKeyProps('timebar')} />;
        if (this.props.type === 'sample') {
            controls = (<div key={this.generateChildKey('prayerControls')} className='prayerControls'>
                {playButton}
                {pauseButton}
                {timeBar}
            </div>);
        } else {
            controls = (<div key={this.generateChildKey('prayerControls')} className='prayerControls'>
                {playButton}
                {pauseButton}
                {timeBar}
            </div>);
            // + nav bar, skip intro, etc.
        }

        return (
            <div key={this.generateChildKey('prayerPlayer')} className='prayerPlayer'>
                <audio key={this.generateChildKey('audio')} onTimeUpdate={this.handleTimeUpdates} id={this.props.id + '_audio'} ref={c => this._audio = c}>
                    <source key={this.generateChildKey('audioSource')} src={this.props.url}/>
                </audio>
                {controls}
            </div>
        )
    }
}

export default HallowAudio;